import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Coins, LogOut } from 'lucide-react'; // ✅ 引入 LogOut
import { 
  type ProjectFile, type FileVersion, type ChatMessage, type UserWallet,
  getFileVersions, getVersionDetail, getVersionMessages, 
  createVersion, saveFileVersion, publishVersion,
  getChatWebSocketUrl, getWallet, deleteMessage,
  getVersionPublishInfo
} from '../../../../lib/apis_vibe';
import ChatPanel from './ChatPanel';
import EditorPanel from './EditorPanel';
import PublishModal from './PublishModal';
import PublishPrepareModal from './PublishPrepareModal';
import { useAuth } from '../../../../contexts/AuthContext'; // ✅ 引入 Auth
import logo from '../../../../assets/imgs/logo.png';

interface WorkspaceProps {
  file: ProjectFile;
  onBack: () => void;
}

const Workspace: React.FC<WorkspaceProps> = ({ file, onBack }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout, user } = useAuth();

  // --- 状态 ---
  const [versions, setVersions] = useState<FileVersion[]>([]);
  const [currentVersion, setCurrentVersion] = useState<FileVersion | null>(null);
  const [htmlCode, setHtmlCode] = useState(''); 
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [wallet, setWallet] = useState<UserWallet | null>(null);
  // 走马灯字幕：reasoning/progress 不存入消息，独立状态显示
  const [currentReasoning, setCurrentReasoning] = useState('');

  // 发布相关状态
  const [lastPublishedUrl, setLastPublishedUrl] = useState<string | null>(null);
  const [lastPublishedImgUrl, setLastPublishedImgUrl] = useState<string | null>(null);
  const [lastPublishedAudioUrl, setLastPublishedAudioUrl] = useState<string | null>(null);
  const [lastPublishedAudioMsg, setLastPublishedAudioMsg] = useState<string | null>(null);
  const [lastPublishedVideoUrl, setLastPublishedVideoUrl] = useState<string | null>(null);
  const [lastPublishedVideo1stUrl, setLastPublishedVideo1stUrl] = useState<string | null>(null);
  const [lastPublishedQrUrl, setLastPublishedQrUrl] = useState<string | null>(null);
  const [lastPublishedFileName, setLastPublishedFileName] = useState<string | null>(null);
  const [lastPublishedFirstMessage, setLastPublishedFirstMessage] = useState<string | null>(null);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isPublishPrepareModalOpen, setIsPublishPrepareModalOpen] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);

  // --- 1. 初始化 ---
  useEffect(() => {
    loadVersionsAndInit();
    fetchWallet();

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [file.id]);

  const loadVersionsAndInit = async () => {
    try {
      const res = await getFileVersions(file.id);
      const list = (res as any).data || res;
      setVersions(list);
      
      if (list.length > 0) {
        const urlVersionId = searchParams.get('version');
        let targetVersionId = list[0].id;

        if (urlVersionId) {
          const vid = parseInt(urlVersionId);
          const exists = list.find((v: FileVersion) => v.id === vid);
          if (exists) targetVersionId = vid;
        }

        handleSwitchVersion(targetVersionId);
      }
    } catch (e) { console.error(e); }
  };

  const fetchWallet = async () => {
    try {
      const res = await getWallet();
      const data = (res as any).data || res;
      setWallet(data);
    } catch (e) { console.error(e); }
  };

  // --- 2. 切换版本 ---
  const handleSwitchVersion = async (versionId: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('version', versionId.toString());
      return newParams;
    });

    if (wsRef.current) {
      wsRef.current.close();
      setIsConnected(false);
    }

    try {
      const verRes = await getVersionDetail(versionId);
      const versionData = (verRes as any).data || verRes;
      setCurrentVersion(versionData);
      setHtmlCode(versionData.html_code || ''); 

      // 检查版本对象是否有 oss_url
      if (versionData.oss_url) {
        setLastPublishedUrl(versionData.oss_url);
      } else {
        setLastPublishedUrl(null);
      }

      const msgRes = await getVersionMessages(versionId);
      const msgList = (msgRes as any).data || msgRes;
      setMessages(msgList);

      connectWebSocket(versionId);

    } catch (e) { console.error(e); }
  };

  // --- 3. WebSocket ---
  const connectWebSocket = (versionId: number) => {
    if (wsRef.current) wsRef.current.close();

    let token = localStorage.getItem('token') || '';
    try {
      const tokenObj = JSON.parse(token);
      if (tokenObj && tokenObj.value) token = tokenObj.value;
    } catch (e) { /* ignore */ }

    const url = getChatWebSocketUrl(versionId, token);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setIsConnected(true);
    ws.onclose = () => setIsConnected(false);
    ws.onerror = () => { setIsAiThinking(false); setIsConnected(false); };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // 走马灯字幕：reasoning / progress / continuation_waiting 不存入消息，独立状态显示
        if (data.type === 'reasoning' || data.type === 'progress' || data.type === 'continuation_waiting') {
          setCurrentReasoning(data.content || '');
        }
        else if (data.type === 'chunk') {
          setIsAiThinking(false);
          setCurrentReasoning('');
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant') {
              return [...prev.slice(0, -1), { ...last, content: last.content + data.content }];
            } else {
              return [...prev, {
                id: -1,
                version_id: versionId,
                role: 'assistant',
                content: data.content,
                input_tokens: 0,
                output_tokens: 0,
                input_money: 0,
                output_money: 0,
                created_at: new Date().toISOString(),
                files: []
              }];
            }
          });
        }
        else if (data.type === 'image') {
          setIsAiThinking(false);
          setCurrentReasoning('');
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant') {
              const newFiles = [...(last.files || []), {
                oss_url: data.image_url,
                file_type: 'image_output'
              }];
              return [...prev.slice(0, -1), { ...last, files: newFiles as any }];
            } else {
              return [...prev, {
                id: -1,
                version_id: versionId,
                role: 'assistant',
                content: '',
                input_tokens: 0,
                output_tokens: 0,
                input_money: 0,
                output_money: 0,
                created_at: new Date().toISOString(),
                files: [{ oss_url: data.image_url, file_type: 'image_output' }] as any
              }];
            }
          });
        }
        else if (data.type === 'meta') {
          setCurrentReasoning('');
          if (data.partial === true) {
            console.warn(`[Vibe Workspace] 内容可能不完整: partial=true, reason=${data.partial_reason || 'unknown'}`);
          }
          setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last && last.role === 'assistant') {
                  return [...prev.slice(0, -1), {
                      ...last,
                      id: data.id,
                      partial: data.partial,
                      partialReason: data.partial_reason,
                      input_tokens: data.input_tokens,
                      output_tokens: data.output_tokens,
                      input_money: data.input_money,
                      output_money: data.output_money,
                      total_money: data.total_money
                  }];
              }
              return prev;
          });
          fetchWallet();
        }
        else if (data.type === 'stop_ack') {
          setIsAiThinking(false);
          setCurrentReasoning('');
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant') {
              return [...prev.slice(0, -1), {
                ...last,
                id: data.id ?? last.id,
                input_tokens: data.input_tokens ?? 0,
                output_tokens: data.output_tokens ?? 0,
                input_money: data.input_money ?? 0,
                output_money: data.output_money ?? 0,
                total_money: data.total_money ?? 0,
                partial: data.partial ?? true,
                partialReason: data.partial_reason ?? 'user_cancelled',
              }];
            }
            return prev;
          });
          fetchWallet();
          // 关闭旧 WS，重连新 WS，等待新聊天
          setTimeout(() => {
            if (currentVersion?.id) {
              connectWebSocket(currentVersion.id);
            }
          }, 100);
        }
        else if (data.type === 'error') {
          setIsAiThinking(false);
          setCurrentReasoning('');
          alert('AI Error: ' + data.content);
        }
      } catch (err) {
        console.error("WS Message Error", err);
      }
    };
  };

  const handleSendMessage = (text: string, images: string[] = [], model: 'gemini' | 'nanobanana' = 'gemini', help: string = 'vibe_coding') => {
    if (!wsRef.current || !isConnected) {
        alert("连接断开，请刷新页面");
        return;
    }
    
    const userMsg: ChatMessage = {
        id: Date.now(),
        version_id: currentVersion?.id || 0,
        role: 'user',
        content: text,
        image_urls: images,
        files: images.map(url => ({ oss_url: url, file_type: 'image_input' })) as any,
        input_tokens: 0,
        output_tokens: 0,
        input_money: 0,
        output_money: 0,
        created_at: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMsg]);
    setIsAiThinking(true);
    
    wsRef.current.send(JSON.stringify({
      message: text,
      image_urls: images,
      model: model,
      help: help
    }));
  };

  const handleStopGeneration = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'stop' }));
  };

  const handleApplyCode = async (code: string) => {
    setHtmlCode(code);
    if (currentVersion) {
      try {
        await saveFileVersion(file.id, currentVersion.id, {
          html_code: code,
          commit_message: "Applied from AI",
          created_by: "user"
        });
      } catch (e) {
        console.error('Auto save after apply failed:', e);
      }
    }
  };

  const handleDeleteMessage = async (messageId: number) => {    
    try {
      const res = await deleteMessage(messageId);
      
      // 找到被删除的AI消息的索引
      const aiMsgIndex = messages.findIndex(m => m.id === messageId);
      // 先删除aiMsgIndex位置的消息
      const aiMsg = messages[aiMsgIndex];
      setMessages(prev=>prev.filter(m=>
        m.id !== aiMsg.id
      ))
      if (aiMsgIndex > 0) {
        // 如果AI消息前面有用户消息，也一并删除
        const userMsg = messages[aiMsgIndex - 1];
        if (userMsg.role === 'user') {
          setMessages(prev => prev.filter(m => 
            !((res as any).data?.deleted_ids?.includes(m.id)) && m.id !== userMsg.id
          ));
          return;
        }
      }
      
      // 只删除当前
      setMessages(prev => prev.filter(m => !((res as any).data?.deleted_ids?.includes(m.id))));
    } catch (err) {
      console.error('Delete message failed:', err);
      alert('删除失败，请稍后重试');
    }
  };

  const handleClearMessages = () => {
    setMessages([]);
  };

  // --- 4. Actions ---
  const handleUpdateCurrentVersion = async () => {
    if (!currentVersion) return;
    try {
      await saveFileVersion(file.id, currentVersion.id, {
        html_code: htmlCode,
        commit_message: "Manual Update",
        created_by: "user"
      });
      alert("✅ 已保存到当前版本！");
    } catch (e) {
      alert("❌ 保存失败");
    }
  };

  const handleCreateNewVersion = async () => {
    if (!currentVersion) return;
    try {
        await createVersion(file.id, {
            html_code: htmlCode,
            commit_message: "New Version from Editor",
            created_by: "user"
        });
        alert("🎉 新版本创建成功！");
        
        const res = await getFileVersions(file.id);
        const list = (res as any).data || res;
        setVersions(list);
        if (list.length > 0) {
            handleSwitchVersion(list[0].id);
        }
    } catch (e) {
        alert("❌ 创建版本失败");
    }
  };

  const handlePublish = async (fileName?: string) => {
    if (!currentVersion) return;
    try {
      await saveFileVersion(file.id, currentVersion.id, {
        html_code: htmlCode,
        commit_message: "Auto save before publish",
        created_by: "user"
      });

      const res = await publishVersion(currentVersion.id, fileName);
      const result = (res as any).data || res;
      
      setLastPublishedUrl(result.oss_url);
      setLastPublishedImgUrl(result.img_url || null);
      setLastPublishedAudioUrl(result.audio_url || null);
      setLastPublishedAudioMsg(result.audio_msg || null);
      setLastPublishedVideoUrl(result.video_url || null);
      setLastPublishedVideo1stUrl(result.video_1st_url || null);
      setLastPublishedQrUrl(result.qr_url || null);
      setLastPublishedFileName(result.file_name || null);
      setIsPublishModalOpen(true);
      
    } catch (e) {
      console.error(e);
      alert("发布失败，请检查网络或配置");
    }
  };

  const handleOpenPublishPrepare = async () => {
    setIsPublishPrepareModalOpen(true);
  };

  const handleShowPublishUrl = async () => {
    if (!currentVersion) return;
    
    try {
      const res = await getVersionPublishInfo(currentVersion.id);
      const result = (res as any).data || res;
      
      setLastPublishedUrl(result.oss_url);
      setLastPublishedImgUrl(result.img_url || null);
      setLastPublishedAudioUrl(result.audio_url || null);
      setLastPublishedAudioMsg(result.audio_msg || null);
      setLastPublishedVideoUrl(result.video_url || null);
      setLastPublishedVideo1stUrl(result.video_1st_url || null);
      setLastPublishedQrUrl(result.qr_url || null);
      setLastPublishedFileName(result.file_name || null);
      setLastPublishedFirstMessage(result.first_message_content || null);
      setIsPublishModalOpen(true);
    } catch (e) {
      console.error('Failed to get publish info:', e);
      alert('获取发布信息失败');
    }
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-black text-white overflow-hidden">
      <header className="h-14 border-b border-white/10 flex items-center justify-between px-4 bg-[#0c0c0e] shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-sm font-bold text-white">{file.name}</h1>
            <div className="flex items-center gap-2 text-[10px] text-gray-500">
                <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                {isConnected ? '已连接 Vibe Server' : '连接断开'}
            </div>
          </div>
        </div>
        
        {/* Logo */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center">
          <div className="bg-white rounded-lg p-2">
            <img src={logo} alt="Logo" className="h-10 w-auto" />
          </div>
        </div>
        
        {/* 右侧工具栏：钱包 + 退出 */}
        <div className="flex items-center gap-4">
          {wallet && (
            <div className="flex items-center gap-4 bg-white/5 px-3 py-1.5 rounded-full border border-white/10 text-xs">
              <div className="flex items-center gap-1.5 text-yellow-400 font-medium">
                <Coins size={14} />
                <span>{wallet.balance.toLocaleString()}</span>
              </div>
              <div className="w-[1px] h-3 bg-white/20"></div>
              <div className="text-gray-400">
                已用: <span className="text-gray-300">{wallet.total_used.toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* ✅ 退出按钮 */}
          <button 
            onClick={logout}
            className="p-2 text-gray-400 hover:text-red-400 hover:bg-white/5 rounded-lg transition-colors"
            title="退出登录"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <ChatPanel
            messages={messages}
            onSendMessage={handleSendMessage}
            onApplyCode={handleApplyCode}
            isAiThinking={isAiThinking}
            tokens={wallet?.balance || 0}
            onDeleteMessage={handleDeleteMessage}
            onClearMessages={handleClearMessages}
            versionId={currentVersion?.id}
            userRole={user?.role}
            currentReasoning={currentReasoning}
            onStopGeneration={handleStopGeneration}
        />
        
        <EditorPanel 
            versions={versions}
            currentVersion={currentVersion}
            htmlCode={htmlCode}
            onCodeChange={setHtmlCode}
            
            onSaveCurrent={handleUpdateCurrentVersion}
            onCreateVersion={handleCreateNewVersion}
            onPublish={handleOpenPublishPrepare}
            onChangeVersion={handleSwitchVersion}
            
            publishedUrl={lastPublishedUrl}
            onShowPublishUrl={handleShowPublishUrl}
            versionId={currentVersion?.id}
        />
      </div>

      <PublishPrepareModal 
        isOpen={isPublishPrepareModalOpen}
        onClose={() => setIsPublishPrepareModalOpen(false)}
        versionId={currentVersion?.id || null}
        fileId={file?.id || null}
        onPublish={handlePublish}
      />

      <PublishModal 
        isOpen={isPublishModalOpen}
        ossUrl={lastPublishedUrl || ''}
        imgUrl={lastPublishedImgUrl}
        audioUrl={lastPublishedAudioUrl}
        audioMsg={lastPublishedAudioMsg}
        videoUrl={lastPublishedVideoUrl}
        video1stUrl={lastPublishedVideo1stUrl}
        qrUrl={lastPublishedQrUrl}
        fileName={lastPublishedFileName ?? undefined}
        firstMessageContent={lastPublishedFirstMessage ?? undefined}
        onClose={() => setIsPublishModalOpen(false)} 
      />

      
    </div>
  );
};

export default Workspace;
